import { DataTable, TruncCell } from "@/components/common/DataTable";
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Building2, Plus, Trash2, LogIn, Pencil, MoreHorizontal, Search, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { logSuperadminAction } from "@/lib/superadminAudit";

interface Company {
  id: string;
  name: string;
  created_at: string;
}

export default function CompaniesAdmin() {
  const { isSuperadmin, isLoading } = useIsSuperadmin();
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [memberships, setMemberships] = useState<{ company_id: string; role: string }[]>([]);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<"name" | "members" | "created">("name");
  const [busy, setBusy] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");

  const [deleteCompany, setDeleteCompany] = useState<Company | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [renameCompany, setRenameCompany] = useState<Company | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const loadCompanies = async () => {
    const [{ data }, { data: members }] = await Promise.all([
      supabase.from("companies").select("*").order("name"),
      supabase.from("user_companies").select("company_id, role"),
    ]);
    setCompanies((data as Company[]) || []);
    setMemberships(members ?? []);
  };

  useEffect(() => {
    if (!isSuperadmin) return;
    loadCompanies();
  }, [isSuperadmin]);

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (!isSuperadmin) return <Navigate to="/admin" replace />;

  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) return;
    setBusy(true);
    try {
      const trimmed = newCompanyName.trim();
      const { data: created, error } = await supabase
        .from("companies")
        .insert({ name: trimmed })
        .select("id, name")
        .single();
      if (error) throw error;
      await logSuperadminAction({
        action: "company_created",
        targetCompany: created ? { id: created.id, name: created.name } : null,
      });
      toast.success(`Company "${trimmed}" created`);
      setNewCompanyName("");
      setCreateOpen(false);
      await loadCompanies();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to create company");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!deleteCompany) return;
    if (deleteConfirmText !== deleteCompany.name) {
      toast.error("Type the company name exactly to confirm");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.from("companies").delete().eq("id", deleteCompany.id);
      if (error) throw error;
      await logSuperadminAction({
        action: "company_deleted",
        targetCompany: { id: deleteCompany.id, name: deleteCompany.name },
      });
      toast.success(`Company "${deleteCompany.name}" deleted`);
      setDeleteCompany(null);
      setDeleteConfirmText("");
      await loadCompanies();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to delete company");
    } finally {
      setBusy(false);
    }
  };

  const handleRenameCompany = async () => {
    if (!renameCompany) return;
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === renameCompany.name) {
      setRenameCompany(null);
      return;
    }
    setBusy(true);
    try {
      const oldName = renameCompany.name;
      const { error } = await supabase
        .from("companies")
        .update({ name: trimmed })
        .eq("id", renameCompany.id);
      if (error) throw error;
      await logSuperadminAction({
        action: "company_renamed",
        targetCompany: { id: renameCompany.id, name: trimmed },
        metadata: { old_name: oldName, new_name: trimmed },
      });
      toast.success(`Renamed to "${trimmed}"`);
      setRenameCompany(null);
      setRenameValue("");
      await loadCompanies();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to rename company");
    } finally {
      setBusy(false);
    }
  };

  const handleOpenAs = async (company: Company) => {
    try {
      if (user) {
        await supabase
          .from("user_companies")
          .upsert({ user_id: user.id, company_id: company.id }, { onConflict: "user_id,company_id" });
      }
      const { error } = await supabase.rpc("set_active_company", { _company_id: company.id });
      if (error) throw error;
      await logSuperadminAction({
        action: "tenant_switched",
        targetCompany: { id: company.id, name: company.name },
      });
      toast.success(`Switched to ${company.name}`);
      window.location.href = "/";
    } catch (err: any) {
      toast.error(err.message ?? "Failed to switch company");
    }
  };

  const count = (id: string, role?: string) => memberships.filter((m) => m.company_id === id && (!role || m.role === role)).length;
  const visible = companies.filter((c) => c.name.toLowerCase().includes(query.trim().toLowerCase())).sort((a, b) =>
    sort === "members" ? count(b.id) - count(a.id) || a.name.localeCompare(b.name) : sort === "created" ? new Date(b.created_at).getTime() - new Date(a.created_at).getTime() : a.name.localeCompare(b.name));

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <Building2 className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">Companies</h1>
          <Badge variant="secondary" className="ml-1">Platform-scoped</Badge>
        </div>
        <p className="text-muted-foreground">
          Select a company to manage its users, features and workflows.
        </p>
      </div>

      <section className="px-4 sm:px-6 mb-8">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Companies</h2>
            <Badge variant="secondary">{companies.length}</Badge>
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-1" /> New company
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="relative flex-1 min-w-[180px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input aria-label="Search companies" placeholder="Search companies" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" /></div>
        </div>
        <DataTable<(typeof companies)[number]>
          storageKey="platform-companies-column-widths"
          rows={visible}
          rowKey={(c) => c.id}
          empty={companies.length === 0 ? "No companies yet." : "No companies match your search."}
          defaultSort={{ key: "name", asc: true }}
          onRowClick={(c) => navigate(`/platform/companies/${c.id}`)}
          columns={[
            { key: "name", header: "Company", width: 300, sortValue: (c) => c.name.toLowerCase(),
              cell: (c) => <span className="flex items-center gap-1 min-w-0"><TruncCell className="font-medium">{c.name}</TruncCell><ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" /></span> },
            { key: "members", header: "Members", width: 110, align: "right", sortValue: (c) => count(c.id),
              cell: (c) => <span className="tabular-nums">{count(c.id)}</span> },
            { key: "admins", header: "Admins", width: 110, align: "right", sortValue: (c) => count(c.id, "admin"),
              cell: (c) => <span className="tabular-nums">{count(c.id, "admin")}</span> },
            { key: "created", header: "Created", width: 130, sortValue: (c) => c.created_at,
              cell: (c) => <span className="text-sm text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</span> },
          ]}
          actions={(c) => (
            <DropdownMenu><DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" aria-label={`More actions for ${c.name}`}><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger><DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleOpenAs(c)}><LogIn className="w-4 h-4 mr-2" />Open as</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setRenameCompany(c); setRenameValue(c.name); }}><Pencil className="w-4 h-4 mr-2" />Rename</DropdownMenuItem>
                <DropdownMenuItem className="text-destructive" onClick={() => { setDeleteCompany(c); setDeleteConfirmText(""); }}><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
              </DropdownMenuContent></DropdownMenu>
          )}
        />
      </section>


      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create new company</DialogTitle>
          </DialogHeader>
          <Input
            value={newCompanyName}
            onChange={(e) => setNewCompanyName(e.target.value)}
            placeholder="Acme NL"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCompany} disabled={busy || !newCompanyName.trim()}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameCompany} onOpenChange={(o) => !o && setRenameCompany(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename company</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder={renameCompany?.name}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRenameCompany();
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameCompany(null)}>Cancel</Button>
            <Button
              onClick={handleRenameCompany}
              disabled={busy || !renameValue.trim() || renameValue.trim() === renameCompany?.name}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteCompany} onOpenChange={(o) => !o && setDeleteCompany(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete {deleteCompany?.name}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This permanently deletes the company and unlinks all members. Tenant data referenced by
            this company may become inaccessible. Type the company name to confirm.
          </p>
          <Input
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={deleteCompany?.name}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCompany(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDeleteCompany}
              disabled={busy || deleteConfirmText !== (deleteCompany?.name ?? "")}
            >
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
